import { useEffect, useRef } from 'react';
import HotelDistances from '@/components/organisms/hotel-distances/hotel-distances';
import SearchWidget, {
  SearchWidgetProps,
} from '@/components/organisms/search-widget/search-widget';
import TextDescription from '@/components/organisms/text-description/text-description';
import TripHighlights, {
  TripHighlightsProps,
} from '@/components/organisms/trip-highlights/trip-highlights';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import Row from '@/components/utilities/row/row';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import parseDanishDate from '@/libraries/formatters/parse-danish-date';
import useIsInView from '@/libraries/hooks/use-is-in-view';
import styles from './booking-overview.module.scss';

export interface BookingOverviewProps extends ModuleWrapperProps {
  /** The headline of the description */
  description: string;
  /** The facility tags to show */
  facilityTags: string[];
  /** The hotel facts to show */
  facts: Content.Components['HotelFactsDto'];
  /** The HTML description */
  htmlDescription: string;
  /** Search settings for the search bar */
  searchSettings: Content.Components['SearchSettingsDto'];
  /** Search widget data */
  searchWidget: Content.Components['SearchWidgetDto'];
  /** The trip highlights */
  tripHighlights?: TripHighlightsProps;
}

const BookingOverview = ({
  description,
  facilityTags,
  facts,
  htmlDescription,
  searchSettings,
  searchWidget,
  tripHighlights,
  ...rest
}: BookingOverviewProps) => {
  const formatSearchWidgetData = (
    searchWidgetData: Content.Properties.SearchWidget,
  ): SearchWidgetProps => {
    return {
      configurationFlowAnchorLinkId:
        searchWidgetData.configurationFlowAnchorLinkId,
      defaultValues: {
        adultsNumber:
          searchSettings.selectedPaxDistribution?.adultsNo?.toString(),
        departureDate: searchSettings.selectedDepartureDate
          ? parseDanishDate(searchSettings.selectedDepartureDate.toString())
          : undefined,
        departureId: searchSettings.selectedDepartureId?.toString(),
        destinationIds: searchSettings.selectedDestinationIds?.map(id =>
          id.toString(),
        ),
        durationType: searchSettings.selectedPeriodType,
      },
      discount: searchWidgetData.discount,
      discountPerPerson: searchWidgetData.discountPerPerson,
      duration: searchWidgetData.duration,
      hubspotForm: searchWidget.hubSpotForm,
      hubspotFormButtonText: searchWidget.hubSpotFormButtonText,
      includedProducts: searchWidgetData.includedProducts,
      infoLinks: searchWidget.link ? [searchWidget.link] : [],
      normalPrice: searchWidgetData.normalPrice,
      normalPricePerPerson: searchWidgetData.normalPricePerPerson,
      price: searchWidgetData.totalPrice,
      priceDetails: searchWidgetData.priceDetails,
      pricePerPerson: searchWidgetData.totalPricePerPerson,
      searchSettings,
    };
  };
  const { setSearchWidgetRefPosition } = usePageDetailsContext();

  const searchWidgetRef = useRef<HTMLDivElement>(null);
  const { position: searchWidgetPosition } = useIsInView(searchWidgetRef, {
    rootMargin: `0px 0px 0px 0px`,
  });

  useEffect(() => {
    setSearchWidgetRefPosition(searchWidgetPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchWidgetPosition]);

  return (
    <ModuleWrapper {...rest} className={styles.component}>
      <Container>
        <Row>
          <Column
            className={styles.column}
            width={{
              tablet: 7,
            }}
          >
            {(!!description || !!htmlDescription || !!facilityTags) && (
              <TextDescription
                headline={description}
                tags={facilityTags}
                text={htmlDescription}
              />
            )}

            {tripHighlights && <TripHighlights {...tripHighlights} />}

            {!!facts?.distanceFacts?.distances?.length && (
              <HotelDistances
                headline={facts.distanceFacts.headline}
                items={facts.distanceFacts.distances}
              />
            )}
          </Column>

          {searchWidget && (
            <Column offset={{ desktop: 1 }} width={{ desktop: 4, tablet: 5 }}>
              <SearchWidget
                ref={searchWidgetRef}
                {...formatSearchWidgetData(searchWidget)}
              />
            </Column>
          )}
        </Row>
      </Container>
    </ModuleWrapper>
  );
};

BookingOverview.displayName = 'BookingOverview';

export default BookingOverview;
